import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  ShoppingCart, 
  User, 
  Calendar,
  DollarSign,
  Package,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

// Componentes reutilizables
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';

// Servicios
import clientService from '../services/clientService';
import salesService from '../services/salesService';

// Estilos
import '../styles/FormSections.css';

const SalesListPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();
  
  // Estados
  const [sales, setSales] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '', // Por defecto todas
    client: '',
    dateFrom: '',
    dateTo: ''
  });

  // Cargar ventas
  const loadSales = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await salesService.getSales({
        page,
        limit: itemsPerPage,
        search: searchTerm,
        status: filters.status,
        client: filters.client,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      });
      
      setSales(response.sales || []);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando ventas:', error);
      showNotification('Error al cargar las ventas', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage, showNotification]);

  // Cargar clientes
  const loadClients = useCallback(async () => {
    try {
      const response = await clientService.getClients({
        page: 1,
        limit: 100,
        isActive: true
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  }, []);

  // Eliminar venta
  const handleDelete = async (saleId, saleNumber) => {
    const confirmed = window.confirm(
      `¿Estás seguro de que quieres eliminar la venta #${saleNumber}?\n\nEsta acción no se puede deshacer.`
    );
    
    if (confirmed) {
      try {
        await salesService.deleteSale(saleId);
        showNotification('Venta eliminada exitosamente', 'success');
        loadSales(currentPage);
      } catch (error) {
        console.error('Error eliminando venta:', error);
        showNotification(error.message || 'Error al eliminar la venta', 'error');
      }
    }
  };

  // Manejar filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ status: '', client: '', dateFrom: '', dateTo: '' });
    setCurrentPage(1);
  };

  // Navegación de páginas
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadSales(newPage);
    }
  };

  // Cambiar página anterior
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  // Cambiar página siguiente
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Manejar cambio de registros por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadSales(1);
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadSales(1);
    loadClients();
  }, [loadSales, loadClients]);

  // Recargar cuando cambien los filtros o búsqueda
  useEffect(() => {
    if (currentPage === 1) {
      loadSales(1);
    } else {
      setCurrentPage(1);
      loadSales(1);
    }
  }, [searchTerm, filters]);

  // Obtener estado de la venta
  const getSaleStatus = (status) => {
    const statusConfig = {
      'PENDING': { label: 'Pendiente', icon: Clock, badgeStyle: { backgroundColor: '#FEF3C7', color: '#D97706', border: '1px solid #FCD34D' } },
      'CONFIRMED': { label: 'Confirmada', icon: CheckCircle, badgeStyle: { backgroundColor: '#D1FAE5', color: '#059669', border: '1px solid #6EE7B7' } },
      'CANCELLED': { label: 'Cancelada', icon: XCircle, badgeStyle: { backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FCA5A5' } },
      'COMPLETED': { label: 'Completada', icon: CheckCircle, badgeStyle: { backgroundColor: '#DBEAFE', color: '#2563EB', border: '1px solid #93C5FD' } }
    };
    return statusConfig[status] || statusConfig['PENDING'];
  };

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingCart className="text-green-600" size={32} />
            Ventas
          </h1>
          <p className="text-gray-600 mt-1">Gestiona las órdenes de venta y servicios</p>
        </div>
        <button
          onClick={() => navigate('/sales/new')}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Nueva Venta
        </button>
      </div>

      {/* Contenedor de filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Campo de búsqueda */}
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Número de venta, cliente..."
                className="form-input"
              />
            </div>

            {/* Filtro por cliente */}
            <div className="w-48">
              <CustomSelect
                label="Cliente"
                value={filters.client}
                onChange={(value) => handleFilterChange('client', value)}
                options={[
                  { value: '', label: 'Todos los clientes' },
                  ...clients.map(client => ({ value: client._id, label: client.name }))
                ]}
                placeholder="Todos los clientes"
              />
            </div>

            {/* Filtro por estado */}
            <div className="w-48">
              <CustomSelect
                label="Estado"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: '', label: 'Todos los estados' },
                  { value: 'PENDING', label: 'Pendiente' },
                  { value: 'CONFIRMED', label: 'Confirmada' },
                  { value: 'COMPLETED', label: 'Completada' },
                  { value: 'CANCELLED', label: 'Cancelada' }
                ]}
                placeholder="Todos los estados"
              />
            </div>

            {/* Filtro por fecha desde */}
            <div className="w-48">
              <label className="form-label">Fecha desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Filtro por fecha hasta */}
            <div className="w-48">
              <label className="form-label">Fecha hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Botón limpiar */}
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button
                onClick={clearFilters}
                className="btn btn-outline w-full"
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Selector de registros por página */}
      <div className="records-per-page">
        <label htmlFor="itemsPerPage">Registros por página:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Lista de ventas */}
      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando ventas...</p>
          </div>
        ) : sales.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">🛒</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filters.client || (filters.status && filters.status !== '') || filters.dateFrom || filters.dateTo ? 'No se encontraron resultados' : 'No hay ventas'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filters.client || (filters.status && filters.status !== '') || filters.dateFrom || filters.dateTo ? 
                'No hay ventas que coincidan con los filtros aplicados' : 
                'Comienza creando tu primera venta'
              }
            </p>
            {!searchTerm && !filters.client && (!filters.status || filters.status === '') && !filters.dateFrom && !filters.dateTo && (
              <button
                onClick={() => navigate('/sales/new')}
                className="btn btn-primary"
              >
                <Plus size={16} />
                Crear Primera Venta
              </button>
            )}
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Número</th>
                <th>Cliente</th>
                <th>Servicios</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Factura</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => {
                const StatusIcon = getSaleStatus(sale.status).icon;
                const statusConfig = getSaleStatus(sale.status);
                
                return (
                  <tr key={sale._id}>
                    <td>
                      <div className="font-medium text-gray-900">
                        #{sale.saleNumber || sale._id.slice(-6)}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400" />
                        <span className="font-medium">{sale.client?.name || 'Cliente'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <Package size={16} className="text-gray-400" />
                        <span>{sale.items?.length || 0} servicios</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 font-medium text-green-600">
                        <DollarSign size={16} />
                        ${sale.total?.toLocaleString() || '0'}
                      </div>
                    </td>
                    <td>
                      <span 
                        style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '6px', 
                          padding: '4px 12px', 
                          borderRadius: '9999px', 
                          fontSize: '0.75rem', 
                          fontWeight: '600', 
                          whiteSpace: 'nowrap',
                          ...statusConfig.badgeStyle 
                        }}
                      >
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td>
                      {sale.invoice ? (
                        <button 
                          onClick={() => navigate(`/invoices/${sale.invoice._id}`)}
                          className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm"
                        >
                          #{sale.invoice.invoiceNumber || 'Factura'}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar size={16} />
                        {new Date(sale.saleDate || sale.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/sales/${sale._id}`)}
                          className="btn btn-primary btn-sm"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/sales/edit/${sale._id}`)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                          title="Editar"
                          disabled={sale.status !== 'PENDING'}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(sale._id, sale.saleNumber)}
                          className="btn btn-danger btn-sm"
                          title="Eliminar"
                          disabled={sale.status !== 'PENDING'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} ventas
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="btn btn-outline btn-sm"
            >
              ← Anterior
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="btn btn-outline btn-sm"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Sistema de notificaciones */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default SalesListPage;